import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { THEME_LABELS, setThemeChoice, toPreference, useTheme, type ThemeChoice } from "@/lib/theme";

/**
 * Přepínač motivu. Volba se hned projeví v prohlížeči a zároveň se uloží na
 * účet — kdyby se čekalo na odpověď serveru, přepnutí by viditelně vázlo.
 * Když uložení selže, motiv zůstane přepnutý pro tuhle relaci; příště ho
 * účet vrátí zpět.
 */
export function ThemeToggle({ testIdPrefix = "theme" }: { testIdPrefix?: string }) {
  const queryClient = useQueryClient();
  const { choice } = useTheme();

  const save = useMutation({
    mutationFn: (next: ThemeChoice) => api.setTheme(toPreference(next)),
    onSuccess: (session) => queryClient.setQueryData(["auth", "me"], session),
  });

  return (
    <div
      className="flex overflow-hidden"
      style={{ border: "1px solid var(--border-default)", borderRadius: "var(--radius-control)" }}
      role="group"
      aria-label={cs.topbar.theme}
    >
      {(["light", "dark", "system"] as ThemeChoice[]).map((option) => {
        const on = choice === option;
        return (
          <button
            key={option}
            type="button"
            className="af-mono flex-1 cursor-pointer px-3 py-1.5 text-[10px] font-bold tracking-[0.12em] transition-colors"
            style={{
              background: on ? "var(--accent-bg)" : "transparent",
              color: on ? "var(--accent-on-tint)" : "var(--text-tertiary)",
            }}
            aria-pressed={on}
            onClick={() => {
              setThemeChoice(option);
              save.mutate(option);
            }}
            data-testid={`${testIdPrefix}-${option}`}
          >
            {THEME_LABELS[option]}
          </button>
        );
      })}
    </div>
  );
}
